import {getLocationPathname} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import * as NavConstants from 'constants/NavConstants';
import {NavPreferences} from 'constants/NavConstants';
import {RegistryPreferences} from 'constants/RegistryConstants';
import * as SourceConstants from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {isBrowser} from 'lib/Browser';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import NavStore from 'nav/NavStore';
import {HOMEPAGE_PATH} from 'nav/sidepanel/homepage/HomepageConstants';
import {shouldShowNewHomepage} from 'nav/sidepanel/homepage/HomepageVisibility';
import * as RegistryActivationFlowActions from 'onboarding/registry-activation-flow/RegistryActivationFlowActions';
import {cleanUpSearchProfilerEvents} from 'performance/processSearchEvents';
import preferences from 'Preferences';
import {refetchSavedSearchesForSection} from 'search/saved-searches/SavedSearchesQuery';
import {NAV_SECTION_TO_SAVED_SEARCH_SECTION} from 'search/saved-searches/SavedSearchUtil';

const {REGISTRY_TYPE} = SourceConstants;

function maybeNavigateToHomepageRoute(): boolean {
  if (shouldShowNewHomepage() && HOMEPAGE_PATH !== getLocationPathname()) {
    navigateToURL(HOMEPAGE_PATH);
    return true;
  }
  return false;
}

export function openSection(section: NavConstants.Section): void {
  /* Open the sidepanel with the given section */
  if (section === NavConstants.SECTIONS.HOME && maybeNavigateToHomepageRoute()) {
    return;
  }

  cleanUpSearchProfilerEvents();
  // If the user has already opened this section, the saved search dropdown won't automatically refresh
  const searchSection = NAV_SECTION_TO_SAVED_SEARCH_SECTION[section];
  if (searchSection) {
    refetchSavedSearchesForSection(searchSection);
  }

  storeLastOpenedSection(section);
  FullScreenActions.hideScreenForRouting();
  AppDispatcher.dispatch({
    actionType: EventConstants.OPEN_SIDEPANEL_WITH_SECTION,
    section,
  });
  if (section === NavConstants.SECTIONS.REGISTRIES) {
    const hasOnboarded = preferences.get(RegistryPreferences.REGISTRY_ACTIVATION_COMPLETE);
    // Note that ENABLE_REGISTRY_ACTIVATION can no longer be set through the application. It remains solely
    // for ad-hoc use.
    const registryActivationEnabled = preferences.get(RegistryPreferences.ENABLE_REGISTRY_ACTIVATION);

    const sources = NavStore.getSources();
    if (!sources) {
      if (registryActivationEnabled && !hasOnboarded) {
        RegistryActivationFlowActions.showStartingScreen('side_panel');
      }
      return;
    }
    const registries = sources.filter((source) => source.sourceType === REGISTRY_TYPE);
    if (registryActivationEnabled && (registries.length === 0 || !hasOnboarded)) {
      RegistryActivationFlowActions.showStartingScreen('side_panel');
    }
  }
}

export function open(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.OPEN_SIDEPANEL_WITH_SECTION,
  });
}

export function setSection(section?: NavConstants.SidepanelSection): void {
  /* Set the currently selected section without changing the expansion state of the side panel */
  storeLastOpenedSection(section);
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_SECTION,
    section,
  });
}

export function close(options: {autoOpenFile: boolean} = {autoOpenFile: true}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_CLOSED,
  });

  if (options.autoOpenFile) {
    FileBrowserActions.openDefaultHomeState();
  }
}

export function expandSection(section: NavConstants.Section): void {
  if (section === NavConstants.SECTIONS.HOME && maybeNavigateToHomepageRoute()) {
    return;
  }

  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_FULLY_EXPANDED,
    section,
  });
}

export function expand(): void {
  // This removes the selection from the window. Without this, when the user types, characters will be added
  // at the previous selection. So if the user has an entry open, expands the sidepanel, then continues to
  // type, more characters will be added to the entry.
  if (isBrowser) {
    window.getSelection()?.removeAllRanges();
  }

  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_FULLY_EXPANDED,
  });
}

export function unexpand(options: {autoOpenFile: boolean} = {autoOpenFile: true}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_SEMI_EXPANDED,
  });

  if (options.autoOpenFile) {
    FileBrowserActions.openDefaultHomeState();
  }
}

export function collapse(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_COLLAPSED,
  });
}

export function uncollapse(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_SEMI_EXPANDED,
  });
}

export function unexpandToSection(section: NavConstants.Section): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SET_SIDEPANEL_SEMI_EXPANDED,
    section,
  });
}

export function openCreateMenu(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_OPEN_CREATE_MENU,
  });
}

export function closeCreateMenu(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_CLOSE_CREATE_MENU,
  });
}

export function openOverflowMenu(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_OPEN_OVERFLOW_MENU,
  });
}

export function closeOverflowMenu(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_CLOSE_OVERFLOW_MENU,
  });
}

export function listAllSources(section: NavConstants.Section): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SIDEPANEL_LIST_ALL_SOURCES,
    section,
  });
}

export function setStorablesSidepanelActiveRegistry(registryId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLES_SIDEPANEL_SET_ACTIVE_REGISTRY,
    registryId,
  });
}

function storeLastOpenedSection(section?: NavConstants.SidepanelSection): void {
  preferences.set(NavPreferences.SIDEPANEL_LAST_OPENED_SECTION, section);
  if (section) {
    // Per sidepanel type, we use localStorage to remember the last section the user was on
    const storageKey = SECTION_TO_SIDEPANEL_STORAGE_KEYS[section];
    if (storageKey) {
      localStorage.setItem(storageKey, section);
    }
  }
}

const SECTION_TO_SIDEPANEL_STORAGE_KEYS: Partial<Record<NavConstants.SidepanelSection, string>> = {
  [NavConstants.Section.REQUESTS_V2_DEFINITIONS]: NavConstants.REQUESTS_V2_SIDEPANEL_TAB_KEY,
  [NavConstants.Section.REQUESTS_V2_SUBMISSIONS]: NavConstants.REQUESTS_V2_SIDEPANEL_TAB_KEY,
};
